import React, { useEffect, useRef, useState } from 'react'
import { DownloadOutlined, FilterFilled, ReloadOutlined, StopOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, Input, Row, Table, Tooltip } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import isUndefined from 'lodash/isUndefined'
import isEqual from 'lodash/isEqual'
import isArray from 'lodash/isArray'
import { useIntl } from 'react-intl'
import ColumnSelector from './column_selector'
import RemoteFilter from './remote_filter'
import useTable from './useTable'
import { api } from '../utils'

const parseDataIndex = path => {
  if (isArray(path)) {
    return path.join('.')
  }

  return path
}

const DataTable = props => {
  const {
    dataSource: { data, loading, pagination },
    columns,
    loadAction,
    loadActionPayload,
    filters,
    showColumnSelector,
    settingsKey,
    customSearch,
    actions,
    rowDoubleClick,
    onClearFilters,
    exportConfig,
    table,
    onRefresh,
    autoRefresh,
    hideTooltipClear,
    hideTooltipRefresh,
    limit,
    ...rest
  } = props

  // eslint-disable-next-line no-unused-vars
  const [ref_table] = useTable(table, {
    getParams: () => getParams(),
    refresh: () => handleRefresh(),
  })

  const dispatch = useDispatch()
  const intl = useIntl()

  const isMobileView = useSelector(s => s._settings.isMobileView)

  const [params, setParams] = useState({
    search: '',
    page: 1,
    limit: limit ?? 25, // default page size
    // sorter,
    // filters
  })

  const [columnSelector, setColumnSelector] = useState({})

  const prevParams = useRef(params)
  const initialLoad = useRef(true)
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false
      load()
    } else if (!isEqual(params, prevParams)) {
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  // componentDidUpdate()
  const isInitialMount = useRef(true)
  const prevFilters = useRef(filters)
  const prevLoadActionPayload = useRef(loadActionPayload)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    } else {
      if (!isEqual(prevFilters.current, filters)) {
        // reload if prop filters have changed, while also clearing previous filters
        clearFilters()
      }

      if (!isEqual(prevLoadActionPayload.current, loadActionPayload)) {
        handlePayloadChange()
      }
    }

    prevFilters.current = filters
    prevLoadActionPayload.current = loadActionPayload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, loadActionPayload])

  const load = () => {
    dispatch(loadAction(getParams()))
  }

  const getParams = () => {
    let newParams = { ...params }

    const defaultSortableColumns = columns.filter(column => column.defaultSortOrder && column.dataIndex)

    // define default sorter, if none already specified by user
    if (defaultSortableColumns.length > 0 && !params.sorter) {
      const col = defaultSortableColumns[0]

      newParams = {
        ...newParams,
        sorter: {
          field: parseDataIndex(col.dataIndex),
          order: col.defaultSortOrder === 'ascend' ? 'ASC' : 'DESC',
        },
      }
    }

    // if filters have been set as a prop, merge with the ones from state
    if (filters) {
      newParams = {
        ...newParams,
        filters: {
          ...newParams.filters,
          ...filters,
        },
      }
    }

    // if extra load action params were set as a prop, merge with those as well
    /* 
      Preserve state params filters and prop filters
      Merge above with filters from loadActionPayload filters
    */
    if (loadActionPayload) {
      const { filters: filtersFromPayload, ...rest } = loadActionPayload

      newParams = {
        ...newParams,
        ...rest,
      }

      if (filtersFromPayload) {
        newParams = {
          ...newParams,
          filters: {
            ...newParams.filters,
            ...filtersFromPayload,
          },
        }
      }
    }

    return newParams
  }

  const clearFilters = () => {
    const newParams = {
      limit: params.limit,
      page: 1,
      search: '',
    }

    setParams({ ...newParams })
  }

  const handlePayloadChange = () => {
    const newParams = { ...params }

    setParams({ ...newParams, page: 1 })
  }

  const handleTableChange = (pagination, filters, sorter) => {
    // only keep the search value from the old params object, the rest should be rebuilt
    let newParams = {
      search: params.search,
      page: pagination.current,
      limit: pagination.pageSize,
    }

    // only keep non null filters
    const cleanFilters = Object.entries(filters).filter(entry => entry[1] !== null)

    if (cleanFilters.length !== 0) {
      newParams = {
        ...newParams,
        filters: Object.fromEntries(cleanFilters),
      }
    }

    // only store sorter if it's non empty, otherwise the API might fail
    if (sorter.field && sorter.order) {
      newParams = {
        ...newParams,
        sorter: {
          field: sorter.field,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      }
    }

    // persist the final object so we can use it when searching
    setParams({ ...newParams })
  }

  const handleSearch = search => {
    setParams({
      ...params,
      page: 1, // reset current page when searching,
      search,
    })
  }

  const handleAutoRefresh = e => {
    console.log('handleAutoRefresh', e.target.checked)
  }

  const handleClearFilters = () => {
    clearFilters()

    if (onClearFilters) {
      onClearFilters()
    }
  }

  const handleRefresh = () => {
    load()

    if (onRefresh) {
      onRefresh(getParams())
    }
  }

  // Event for <ColumnSelector />
  const onColumnSelectionChanged = columns => {
    setColumnSelector({
      ...columnSelector,
      ...columns,
    })
  }

  const remoteFilterConfig = config => ({
    filterDropdown: ({ setSelectedKeys, confirm }) => (
      <div style={config.styleColumn ? { ...config.styleColumn } : { padding: 8, width: '200px' }}>
        <RemoteFilter
          remoteSearch={config}
          tableDropdownFn={{
            setSelectedKeys,
            confirm,
          }}
        />
      </div>
    ),
    filterIcon: filtered => (
      // <Icon type="filter" theme="filled" style={{ color: filtered ? '#1890ff' : undefined }} />
      <FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  })

  const handleExportClick = () => {
    const { apiFn, filename = 'export.xlsx' } = exportConfig

    const apiConfig = apiFn(getParams())

    api(apiConfig)
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
      })
  }

  const processColumns = columns => {
    // the method aims to hook the columns' sorter and filters to the
    // internal state of our component, so we can easily reset all of them

    const { sorter, filters: filters_p } = params

    const propFilters = filters

    const processedColumns = columns.map(column => {
      // Set hidden attribute by default. Avoid using 'undefined' type
      if (column.hidden !== true) {
        column.hidden = false
      }

      // filter out columns that should be hidden on mobile
      // Will be overwritten by <ColumnSelector  /> if enabled
      if (column.mobile === false && isMobileView) {
        column.hidden = true
      }

      return column
    })

    // Need to make copies of 'processedColumns' to not loose columns
    // For <Table /> we use 'columnsForTable'
    // For <ColumnSelector  /> we use 'processedColumns'

    let columnsForTable = [...processedColumns]

    // columnSelector - Correct hidden attribute by state
    if (Object.keys(columnSelector).length > 0) {
      columnsForTable = columnsForTable.map(column => {
        const lookup = column.dataIndex || column.key

        if (columnSelector[parseDataIndex(lookup)] !== undefined) {
          column.hidden = columnSelector[parseDataIndex(lookup)]
        }

        return column
      })
    }

    // Filter and map <Table /> columns based on hidden prop
    columnsForTable = columnsForTable.filter(column => column.hidden === false)

    columnsForTable = columnsForTable.map(column => {
      if (!sorter) {
        column.sortOrder = false
      }

      if (sorter && parseDataIndex(column.dataIndex) === sorter.field) {
        column.sortOrder = `${sorter.order.toLowerCase()}end` // ascend / descend
      }

      if (filters_p && filters_p[parseDataIndex(column.dataIndex)]) {
        column.filteredValue = filters_p[parseDataIndex(column.dataIndex)]
      } else {
        column.filteredValue = null
      }

      // if a filter has been set via props, prevent user from changing it
      if (propFilters && propFilters[parseDataIndex(column.dataIndex)]) {
        column.filters = []
      }

      if (column.remoteFilter) {
        column = {
          ...column,
          ...remoteFilterConfig(column.remoteFilter),
        }
      }

      return column
    })

    // Return both arrays of columns
    return [processedColumns, columnsForTable]
  }

  const [columnsForColumnSelector, columnsForTable] = processColumns(columns)

  const extra = (
    <Row type="flex" justify="space-between">
      {isUndefined(customSearch) && (
        <Col xs={24} sm={8}>
          <Input.Search
            style={{
              marginBottom: '1.5rem',
            }}
            allowClear
            placeholder={intl.formatMessage({ id: 'datatable.search' })}
            onSearch={handleSearch}
            defaultValue={params.search}
          />
        </Col>
      )}

      {!isUndefined(customSearch) && (
        <Col>
          <div style={{ display: 'inline-flex', marginBottom: '1.5rem' }}>{customSearch}</div>
        </Col>
      )}

      <Col>
        <div style={{ display: 'inline-flex', marginBottom: '1.5rem' }}>
          <Button.Group>
            {!isUndefined(autoRefresh) && <Checkbox onChange={handleAutoRefresh}>AutoRefresh</Checkbox>}
            {!hideTooltipClear && (
              <Tooltip placement="top" title={intl.formatMessage({ id: 'datatable.tooltips.clear' })}>
                <Button icon={<StopOutlined />} onClick={handleClearFilters} />
              </Tooltip>
            )}
            {!hideTooltipRefresh && (
              <Tooltip placement="top" title={intl.formatMessage({ id: 'datatable.tooltips.refresh' })}>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                  {intl.formatMessage({ id: 'datatable.actions.refresh' })}
                </Button>
              </Tooltip>
            )}
          </Button.Group>

          {showColumnSelector && <ColumnSelector style={{ marginLeft: '.5rem' }} onColumnSelectionChanged={onColumnSelectionChanged} columns={columnsForColumnSelector} settingsKey={settingsKey} />}

          {exportConfig && (
            <Tooltip placement="top" title={intl.formatMessage({ id: 'datatable.tooltips.export' })}>
              <Button style={{ marginLeft: '.5rem' }} icon={<DownloadOutlined />} onClick={handleExportClick} />
            </Tooltip>
          )}

          {actions}
        </div>
      </Col>
    </Row>
  )

  return (
    <div>
      {extra}

      <Table
        scroll={{
          x: true,
        }}
        style={{
          whiteSpace: 'nowrap',
        }}
        key={columns.length}
        rowKey="id"
        columns={columnsForTable}
        dataSource={data}
        onChange={handleTableChange}
        loading={loading}
        pagination={{
          showLessItems: true,
          showQuickJumper: true,
          current: params.page,
          defaultPageSize: params.limit,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '25', '50', '100', '200'],
          total: pagination && pagination.total,
        }}
        onRow={(record, rowIndex) => {
          return {
            onDoubleClick: event => {
              if (rowDoubleClick) {
                rowDoubleClick(record, rowIndex, event)
              }
            },
          }
        }}
        {...rest}
      />
    </div>
  )
}

export default DataTable
